import React, { useState } from "react";
import { Modal, Box, Button, Typography, CircularProgress, IconButton, Snackbar, Alert } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useSelector } from "react-redux";
import { useTheme } from "@mui/material/styles";
import PropTypes from "prop-types";
import { useUploadEmailUpdateCsvMutation } from "../../../store/services/response.service";
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';

UpdateEmailCsvModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired
};

const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._'-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
};

const validateCSVContent = async (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => {
            const text = event.target.result;
            const lines = text.split('\n');

            // Skip header row and filter out empty lines more thoroughly
            const dataLines = lines.slice(1).filter(line => {
                const trimmed = line.trim();
                // Filter out completely empty lines and lines with only commas
                return trimmed && trimmed !== ',' && trimmed.replace(/,/g, '').length > 0;
            });

            const errors = [];
            const oldEmails = new Set();

            dataLines.forEach((line, index) => {
                const columns = line.split(',').map(col => col.trim());
                const oldEmail = columns[0];
                const newEmail = columns[1];
                const lineNum = index + 2;

                // Skip validation for rows where both emails are empty (truly empty rows)
                if (!oldEmail && !newEmail) {
                    return; // Skip this row silently
                }

                // Check for missing emails (only if row has partial data)
                if (!oldEmail) {
                    errors.push(`Line ${lineNum}: Missing old email address`);
                }
                if (!newEmail) {
                    errors.push(`Line ${lineNum}: Missing new email address`);
                }

                // Validate email formats
                if (oldEmail && !validateEmail(oldEmail)) {
                    errors.push(`Line ${lineNum}: "${oldEmail}" is not a valid email address (Old Email)`);
                }
                if (newEmail && !validateEmail(newEmail)) {
                    errors.push(`Line ${lineNum}: "${newEmail}" is not a valid email address (New Email)`);
                }

                // Check if old and new emails are the same
                if (oldEmail && newEmail && oldEmail.toLowerCase() === newEmail.toLowerCase()) {
                    errors.push(`Line ${lineNum}: Old email and new email cannot be the same`);
                }

                // Check for duplicate old emails
                if (oldEmail) {
                    const lowerOldEmail = oldEmail.toLowerCase();
                    if (oldEmails.has(lowerOldEmail)) {
                        errors.push(`Line ${lineNum}: Duplicate old email "${oldEmail}"`);
                    }
                    oldEmails.add(lowerOldEmail);
                }
            });

            // Check if file is empty after filtering
            if (dataLines.length === 0) {
                errors.push('CSV file contains no data rows');
            }

            // Check if too many rows
            if (dataLines.length > 500) {
                errors.push(`CSV file contains ${dataLines.length} rows. Maximum is 500 rows.`);
            }

            if (errors.length > 0) {
                reject({ errors });
            } else {
                resolve(true);
            }
        };

        reader.onerror = () => reject({ errors: ['Error reading file'] });
        reader.readAsText(file);
    });
};

export default function UpdateEmailCsvModal({ open, onClose }) {
    const theme = useTheme();
    const { tenancyId } = useSelector((state) => state.tenancy);
    const [csvFile, setCsvFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");
    const [validationErrors, setValidationErrors] = useState([]);

    const [uploadEmailUpdateCsv] = useUploadEmailUpdateCsvMutation();

    const handleFileChange = async (event) => {
        const file = event.target.files[0];
        setCsvFile(file);
        setValidationErrors([]);

        if (file) {
            try {
                await validateCSVContent(file);
                setSnackbarMessage("CSV file validated successfully.");
                setSnackbarSeverity("success");
                setSnackbarOpen(true);
            } catch (error) {
                setValidationErrors(error.errors);
                setSnackbarMessage("Please fix the validation errors before uploading.");
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
            }
        }
    };

    const handleDownloadCSV = () => {
        const csvContent = "OldEmail,NewEmail\nold.email@example.com,new.email@example.com\njohn.old@example.com,john.new@example.com";
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'email-update-template.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const handleUploadCSV = async () => {
        if (!csvFile) {
            setSnackbarMessage("Please select a CSV file.");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            return;
        }

        if (validationErrors.length > 0) {
            setSnackbarMessage("Please fix validation errors before uploading.");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            return;
        }

        setIsUploading(true);
        if (csvFile.type === "application/vnd.ms-excel" || csvFile.type === "text/csv") {
            try {
                const response = await uploadEmailUpdateCsv({ tenancyId, file: csvFile }).unwrap();
                if (response.success) {
                    setSnackbarMessage("Email update CSV uploaded successfully. Emails will be updated automatically.");
                    setSnackbarSeverity("success");
                    setCsvFile(null);
                    setValidationErrors([]);
                } else {
                    setSnackbarMessage("Error uploading email update CSV.");
                    setSnackbarSeverity("error");
                }
            } catch (error) {
                console.error(`Error uploading email update CSV: ${error}`);
                setSnackbarMessage("Error uploading email update CSV.");
                setSnackbarSeverity("error");
            }
        } else {
            setSnackbarMessage("Invalid file type. Please upload a CSV file.");
            setSnackbarSeverity("error");
        }
        setSnackbarOpen(true);
        setIsUploading(false);
        onClose();
    };

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbarOpen(false);
    };

    return (
        <>
            <Modal
                open={open}
                onClose={onClose}
                aria-labelledby="update-email-csv-modal-title"
                aria-describedby="update-email-csv-modal-description"
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: "80%",
                        maxWidth: 650,
                        boxShadow: theme.customShadows.z1,
                        backgroundColor: theme.palette.background.default,
                        borderRadius: theme.borderRadius.sm,
                        p: "26px 32px"
                    }}
                >
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography id="update-email-csv-modal-title" variant="h4" gutterBottom>
                            UPDATE USER EMAILS VIA CSV
                        </Typography>
                        <IconButton onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2" component="div">
                            <strong>CSV Format Requirements:</strong>
                            <br />- Two columns: <strong>OldEmail</strong> and <strong>NewEmail</strong>
                            <br />- Old and new emails cannot be the same
                            <br />- No duplicate old emails allowed
                            <br />- Maximum 500 users per CSV
                        </Typography>
                    </Alert>

                    <input
                        type="file"
                        accept=".csv,application/vnd.ms-excel,text/csv"
                        onChange={handleFileChange}
                        style={{
                            width: "100%",
                            marginBottom: '20px',
                            border: '2px dashed #000',
                            padding: '10px',
                            borderRadius: '4px',
                        }}
                    />

                    {validationErrors.length > 0 && (
                        <Alert
                            severity="error"
                            sx={{
                                mb: 2,
                                '& .MuiAlert-message': {
                                    width: '100%'
                                }
                            }}
                        >
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Validation Errors
                            </Typography>
                            <Box
                                component="ul"
                                sx={{
                                    m: 0,
                                    pl: 2,
                                    maxHeight: '150px',
                                    overflowY: 'auto'
                                }}
                            >
                                {validationErrors.map((error, index) => (
                                    <Typography
                                        component="li"
                                        key={index}
                                        variant="body2"
                                        sx={{ mb: 0.5 }}
                                    >
                                        {error}
                                    </Typography>
                                ))}
                            </Box>
                        </Alert>
                    )}

                    <Box mt={3} display="flex" justifyContent="flex-start">
                        <Button
                            onClick={handleDownloadCSV}
                            variant="outlined"
                            color="inherit"
                            sx={{ mr: 2, flexGrow: 1 }}
                        >
                            Download CSV Template
                            <DownloadIcon sx={{ ml: 1 }} />
                        </Button>
                        <Button
                            onClick={handleUploadCSV}
                            variant="contained"
                            color="primary"
                            disabled={isUploading || validationErrors.length > 0}
                            sx={{ flexGrow: 1 }}
                        >
                            {isUploading ? <CircularProgress size={24} /> : <>Update Emails <UploadIcon sx={{ ml: 1 }} /></>}
                        </Button>
                    </Box>
                </Box>
            </Modal>
            <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
                <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }} variant="filled">
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </>
    );
}